import React from 'react';
import Card from './card';
import { BEER_STYLE_GROUPS, getGroupBarColor } from '../../utils/beerTaxonomy';

const CHARACTERISTIC_ICONS = {
  'Non-Alcoholic': '/non-alcoholic.png',
  'High ABV': '/high-abv.png',
  'Contains Lactose': '/contains-lactose.png',
  'Contains Nuts': '/contains-nuts.png',
  'Gluten-Free': '/gluten-free.png',
  'Vegan': '/vegan.png',
  'Filtered': '/filtered.png',
  'Unfiltered': '/unfiltered.png',
};

const BeerDashboardStats = ({
  total,
  newBeers,
  removed,
  lastUpdated,
  BeerCategory,
  styleDistribution,
  regionalDistribution,
  dietaryConsiderations,
  recentUpdates,
  BeerActivityData = [],
  onScroll,
  BeerActivityTotalCount = 0,
  BeerActivityLoading = false,
  userRole,
  // compact: lay out for narrow containers (e.g. the 500px Manage Menu side
  // panel) where viewport breakpoints would otherwise over-split the grids.
  compact = false
}) => {
  return (
    <div className="space-y-6 text-left mt-6">
      {/* Summary Stats Row */}
      <div className={compact ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"}>
        {/* Total Beers */}
        <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="flex justify-between items-start">
            <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Total Beers</span>
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11 20V9.82C8.63 9.4 6.64 8 5.62 6.01 4.5 3.82 5.09 1.49 5.25 1h13.5c.16.49.75 2.82-.37 5.01-1.02 1.99-3.01 3.39-5.38 3.81V20h3v2H8v-2h3zM7.18 3c-.15 1.05-.2 2.37.58 3.9.72 1.41 2.22 2.15 4.24 2.15s3.52-.74 4.24-2.15c.78-1.53.73-2.85.58-3.9H7.18z" />
            </svg>
          </div>
          <span className="text-[32px] font-black text-gray-900 tracking-tight">{total}</span>
        </Card>

        {/* New Beers */}
        <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="flex justify-between items-start">
            <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">New Beers (30 Days)</span>
            <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center text-white">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
            </div>
          </div>
          <span className="text-[32px] font-black text-gray-900 tracking-tight">{newBeers}</span>
        </Card>

        {/* Removed */}
        <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="flex justify-between items-start">
            <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Removed (30 Days)</span>
            <div className="w-5 h-5 bg-red-500 rounded flex items-center justify-center text-white">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
          </div>
          <span className="text-[32px] font-black text-red-600 tracking-tight">{removed}</span>
        </Card>

        {/* Last Updated */}
        <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="flex justify-between items-start">
            <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Last Updated</span>
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
          </div>
          <span className="text-xl font-black text-gray-900 mt-1">{lastUpdated}</span>
        </Card>
      </div>

      {/* Style Coverage and Characteristics Row */}
      <div className={compact ? "grid grid-cols-1 gap-4 mb-6" : "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6"}>
        <div className={compact ? "space-y-6" : "lg:col-span-2 space-y-6"}>
          {/* Beer Style Coverage */}
          <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl h-full">
            <div className="mb-8">
              <h3 className="text-[16px] font-bold text-gray-900">Beer Style Coverage</h3>
              <p className="text-[12px] text-gray-500 mt-1">Distribution of beers by beer style group and beer style.</p>
            </div>

            <div className="space-y-8">
              {(() => {
                // Look up each core style's actual count from styleDistribution by exact
                // Beer Taxonomy name (falls back to 0 so uncovered styles still render).
                const countByStyle = {};
                (styleDistribution || []).forEach(style => {
                  const name = style.name || style.label || '';
                  countByStyle[name] = (countByStyle[name] || 0) + (style.count || 0);
                });

                const groups = BEER_STYLE_GROUPS.map(group => {
                  const items = group.core_styles
                    .map(styleName => ({ name: styleName, count: countByStyle[styleName] || 0 }))
                    .sort((a, b) => b.count - a.count);
                  const totalCount = items.reduce((sum, item) => sum + item.count, 0);
                  const pct = total > 0 ? Math.round((totalCount / total) * 100) : 0;
                  return { name: group.group_name, items, totalCount, pct };
                });

                return groups.map((group, gIdx) => {
                  const color = getGroupBarColor(group.name);

                  return (
                    <div key={group.name} className="space-y-4">
                      {/* Group Header */}
                      <div className={`flex justify-between items-end pb-2 ${gIdx !== 0 ? 'border-t border-gray-100 pt-6' : ''}`}>
                        <span className="text-[13px] font-bold text-gray-900 uppercase tracking-tight">{group.name}</span>
                        <span className="text-[13px] font-bold text-gray-900">
                          {group.totalCount} <span className="text-gray-500 font-normal">({group.pct}%)</span>
                        </span>
                      </div>

                      {/* Styles in Group */}
                      <div className="space-y-4">
                        {group.items.map(item => {
                          const covered = item.count > 0;
                          const itemPct = group.totalCount > 0 ? Math.round((item.count / group.totalCount) * 100) : 0;
                          return (
                            <div key={item.name} className="space-y-1.5">
                               <div className="flex justify-between items-end">
                                 <span className={`text-[12px] font-medium ${covered ? 'text-gray-500' : 'text-gray-300'}`}>{item.name}</span>
                                 <span className={`text-[12px] font-bold ${covered ? 'text-gray-700' : 'text-gray-300'}`}>
                                   {item.count} <span className={`font-normal ${covered ? 'text-gray-400' : 'text-gray-300'}`}>• {itemPct}%</span>
                                 </span>
                               </div>
                               <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                 {covered && (
                                   <div className="h-full rounded-full" style={{ width: `${itemPct}%`, backgroundColor: color }}></div>
                                 )}
                               </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </Card>
        </div>

        {/* Right Column Stack */}
        <div className="lg:col-span-1 space-y-4 flex flex-col h-full">
          {/* Regional Distribution */}
          <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
            <div className="mb-6">
              <h3 className="text-[16px] font-bold text-gray-900">Regional Distribution</h3>
              <p className="text-[12px] text-gray-500 mt-1">Shows the percentage of beers by region.</p>
            </div>
            <div className="space-y-5">
              {regionalDistribution && regionalDistribution.map((d) => (
                <div key={d.label} className="w-full">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[13px] font-bold text-gray-900">{d.label}</span>
                    <span className="text-[12px] text-gray-500">{d.value}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full rounded-full bg-[#881337]" style={{ width: `${d.value}%` }}></div>
                  </div>
                </div>
              ))}
              {(!regionalDistribution || regionalDistribution.length === 0) && (
                <div className="text-center text-gray-400 text-sm py-4">No region data available</div>
              )}
            </div>
          </Card>

          {/* Beer Characteristics & Composition */}
          <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl h-full">
            <div className="mb-6">
              <h3 className="text-[15px] font-bold text-gray-900">Beer Characteristics & Composition</h3>
              <p className="text-[11px] text-gray-500 mt-1">Breakdown of dietary and composition attributes across your Beer list.</p>
            </div>

            <div className="bg-[#FFF7ED] rounded-xl p-3">
              <div className="grid grid-cols-2 lg:grid-cols-2 gap-2">
                {dietaryConsiderations && dietaryConsiderations.map((char, idx) => {
                  const label = char.label || char.name || '';
                  const raw = String(char.value || char.pct || '0%');
                  const pct = raw.endsWith('%') ? raw.replace('%', '') : raw;
                  const count = char.count || 0;
                  const icon = CHARACTERISTIC_ICONS[label];

                  return (
                    <div key={idx} className="bg-white rounded-xl py-3 px-2 flex flex-col items-center text-center shadow-sm border border-orange-100/50">
                      {icon && <img src={icon} alt={label} className="w-6 h-6 mb-1.5 object-contain" />}
                      <p className="text-[13px] font-bold text-gray-800 leading-tight mb-0.5">{label}</p>
                      <p className="text-[11px] text-gray-500 font-medium">
                        {count} ({pct}%)
                      </p>
                    </div>
                  );
                })}
                {(!dietaryConsiderations || dietaryConsiderations.length === 0) && (
                  <div className="py-8 text-center text-gray-400 text-xs col-span-full italic">
                    No characteristics data available
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Beer Activity Section */}
      {userRole !== 'employee' && (
        <div className="bg-white p-4 sm:p-6 lg:p-8 text-start rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md w-full mt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[17px] font-black text-gray-900 tracking-tight">
              Recent Beer Activity
            </h3>
            {BeerActivityTotalCount > 0 && (
              <span className="text-[12px] font-medium text-gray-400">
                {BeerActivityData.length} of {BeerActivityTotalCount} activities
              </span>
            )}
          </div>

          <div
            onScroll={onScroll}
            className={`space-y-2 ${compact ? "h-[500px]" : "h-[850px]"} overflow-y-auto pr-2 scroll-smooth`}
          >
            {BeerActivityData.length === 0 && !BeerActivityLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-sm font-medium">No activity yet</span>
              </div>
            ) : (
              BeerActivityData.map((u, idx) => {
                const action = u.action || "update";
                const text =
                  u.details?.description ||
                  (action.includes("created")
                    ? "New Beer added"
                    : action.includes("archived")
                      ? "Beer archived"
                      : action.includes("restored")
                        ? "Beer restored"
                        : "Beer item updated");
                const user = u.user_uuid;
                const subtitle = user
                  ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                  : "";
                const hours = Math.floor(
                  (Date.now() - new Date(u.createdAt).getTime()) /
                  (1000 * 60 * 60)
                );
                const time =
                  hours < 24
                    ? `${hours} hrs ago`
                    : `${Math.floor(hours / 24)} days ago`;

                const { icon, bg } = (function () {
                  if (action.includes("created"))
                    return {
                      icon: (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                      ),
                      bg: "bg-green-50",
                    };
                  if (action.includes("archived"))
                    return {
                      icon: (
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      ),
                      bg: "bg-red-50",
                    };
                  if (action.includes("restored"))
                    return {
                      icon: (
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ),
                      bg: "bg-purple-50",
                    };
                  return {
                    icon: (
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    ),
                    bg: "bg-blue-50",
                  };
                })();

                return (
                  <div
                    key={`${u._id || idx}-${idx}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-gray-50 hover:border-[#FDE6D2] hover:bg-[#FFF9F3]/30 transition-all group gap-3"
                  >
                    <div className="flex items-center gap-4 sm:gap-5">
                      <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}>
                        {icon}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] sm:text-[14px] font-black text-gray-900 leading-tight truncate">
                          {text}
                        </span>
                        {subtitle && (
                          <span className="text-[12px] font-medium text-gray-500 mt-1 line-clamp-1">{`By ${subtitle}`}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-[12px] font-bold text-gray-400 whitespace-nowrap self-end sm:self-center">
                      {time}
                    </span>
                  </div>
                );
              })
            )}

            {/* Loading indicator */}
            {BeerActivityLoading && (
              <div className="flex items-center justify-center py-6">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-[#881337] rounded-full animate-spin"></div>
                <span className="ml-3 text-sm font-medium text-gray-500">Loading more...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BeerDashboardStats;
